import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, XCircle, Printer, Download, ArrowUpRight, ArrowDownRight, Award } from "lucide-react";

const DailyReports = ({ dashboardFiles = [], allFiles = [], startDate, endDate, shiftFilter, locationFilter, selectedClient, clientsList = [] }) => {
    const [clientRemarks, setClientRemarks] = useState({});
    const [usersList, setUsersList] = useState([]);
    const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState("All");
    const [deptFilter, setDeptFilter] = useState("All"); // "All" | "QC" | "Revised"

    // Fetch users list to include all active reviewers with locations
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await axios.get("/api/users", {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data && Array.isArray(res.data)) {
                    setUsersList(res.data);
                }
            } catch (err) {
                console.error("Failed to fetch users in DailyReports:", err);
            }
        };
        fetchUsers();
    }, []);

    // Helper: Format MySQL dates safely
    const formatDate = (dateStr) => {
        if (!dateStr || String(dateStr).trim() === "" || String(dateStr).trim() === "-") return "";
        const str = String(dateStr).trim().replace(/\//g, "-");
        const datePart = str.split(" ")[0];
        const parts = datePart.split("-").map(Number);
        if (parts.length === 3) {
            let year, month, day;
            if (parts[0] > 1000) {
                [year, month, day] = parts;
            } else if (parts[2] > 1000) {
                if (parts[0] > 12) {
                    [day, month, year] = parts;
                } else {
                    [month, day, year] = parts;
                }
            }
            if (year && month && day && !isNaN(year) && !isNaN(month) && !isNaN(day)) {
                const mm = String(month).padStart(2, '0');
                const dd = String(day).padStart(2, '0');
                return `${year}-${mm}-${dd}`;
            }
        }
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        const ny = d.getFullYear();
        const nm = String(d.getMonth() + 1).padStart(2, '0');
        const nd = String(d.getDate()).padStart(2, '0');
        return `${ny}-${nm}-${nd}`;
    };

    const isClosedOrExceptionFile = (file) => {
        if (!file) return false;
        const status = String(file.status || "").trim().toLowerCase();
        const substatus = String(file.substatus || "").trim().toLowerCase();

        if (!status && !substatus) return false;

        return (
            status.includes("completed") ||
            status.includes("rejected") ||
            status.includes("do not review") ||
            status.includes("wrong report") ||
            status.includes("complete by client") ||
            substatus.includes("completed") ||
            substatus.includes("rejected") ||
            substatus.includes("do not review") ||
            substatus.includes("wrong report") ||
            substatus.includes("complete by client")
        );
    };

    const selectedStartDateStr = startDate || new Date().toISOString().split("T")[0];
    const selectedEndDateStr = endDate || selectedStartDateStr;

    // Operational Shift-date calculation helper (same logic as Hourly Monitor)
    const getShiftDate = (timeStr, fallbackDate) => {
        if (!timeStr || String(timeStr).trim() === "" || String(timeStr).trim() === "-") {
            return fallbackDate ? formatDate(fallbackDate) : "";
        }
        try {
            const str = String(timeStr).trim().replace(/\//g, "-");
            const parts = str.split(" ");
            const datePart = parts[0];
            const timePart = parts[1] || "00:00:00";
            const hour = parseInt(timePart.split(":")[0], 10) || 0;

            let year, month, day;
            const dateParts = datePart.split("-").map(Number);
            if (dateParts.length === 3) {
                if (dateParts[0] > 1000) {
                    [year, month, day] = dateParts;
                } else if (dateParts[2] > 1000) {
                    if (dateParts[0] > 12) {
                        [day, month, year] = dateParts;
                    } else {
                        [month, day, year] = dateParts;
                    }
                }
            }

            if (!year || !month || !day || isNaN(year) || isNaN(month) || isNaN(day)) {
                const d = new Date(timeStr);
                if (isNaN(d.getTime())) return fallbackDate ? formatDate(fallbackDate) : "";
                year = d.getFullYear();
                month = d.getMonth() + 1;
                day = d.getDate();
                const h = d.getHours();
                if (h >= 17) {
                    d.setDate(d.getDate() + 1);
                }
                const ny = d.getFullYear();
                const nm = String(d.getMonth() + 1).padStart(2, '0');
                const nd = String(d.getDate()).padStart(2, '0');
                return `${ny}-${nm}-${nd}`;
            }

            const dt = new Date(year, month - 1, day);
            if (hour >= 17) {
                dt.setDate(dt.getDate() + 1);
            }
            
            const ny = dt.getFullYear();
            const nm = String(dt.getMonth() + 1).padStart(2, '0');
            const nd = String(dt.getDate()).padStart(2, '0');
            return `${ny}-${nm}-${nd}`;
        } catch {
            return fallbackDate ? formatDate(fallbackDate) : "";
        }
    };

    // Strict in_time date formatting helper (no shift/rollover offset)
    const getStrictInTimeDate = (timeStr) => {
        if (!timeStr || String(timeStr).trim() === "" || String(timeStr).trim() === "-") {
            return "";
        }
        try {
            const str = String(timeStr).trim();
            const cleanStr = str.replace(/\//g, "-");
            const dateTimeParts = cleanStr.split(" ");
            const datePart = dateTimeParts[0];
            
            let year, month, day;
            if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                [year, month, day] = datePart.split("-").map(Number);
            } else if (/^\d{2}-\d{2}-\d{4}$/.test(datePart)) {
                const [p1, p2, p3] = datePart.split("-").map(Number);
                if (p2 > 12) {
                    [month, day, year] = [p1, p2, p3];
                } else {
                    [day, month, year] = [p1, p2, p3];
                }
            } else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(datePart)) {
                const [p1, p2, p3] = datePart.split("-").map(Number);
                if (p2 > 12) {
                    [month, day, year] = [p1, p2, p3];
                } else {
                    [day, month, year] = [p1, p2, p3];
                }
            } else {
                const d = new Date(timeStr);
                if (isNaN(d.getTime())) return "";
                year = d.getFullYear();
                month = d.getMonth() + 1;
                day = d.getDate();
            }
            const nm = String(month).padStart(2, '0');
            const nd = String(day).padStart(2, '0');
            return `${year}-${nm}-${nd}`;
        } catch {
            return "";
        }
    };

    const getHourFromTime = (timeStr) => {
        if (!timeStr || String(timeStr).trim() === "" || String(timeStr).trim() === "-") return null;
        try {
            const str = String(timeStr).trim();
            if (str.includes("T")) {
                const timePart = str.split("T")[1];
                return parseInt(timePart.split(":")[0], 10);
            }
            const parts = str.split(" ");
            const timePart = parts[1] || parts[0];
            if (timePart && timePart.includes(":")) {
                return parseInt(timePart.split(":")[0], 10);
            }
        } catch {}
        return null;
    };

    // Helper to verify shift and location boundaries
    const matchesFilters = (f, timeField = "in_time") => {
        // Shift filter (Live / Offline)
        if (shiftFilter && shiftFilter !== "Total" && shiftFilter !== "All") {
            const fileShift = f.shift ? String(f.shift).toLowerCase().trim() : "";
            if (fileShift) {
                if (fileShift !== shiftFilter.toLowerCase().trim()) return false;
            } else {
                const checkTime = f[timeField] || f.out_time || f.in_time;
                if (checkTime) {
                    const hour = getHourFromTime(checkTime);
                    if (hour !== null) {
                        if (shiftFilter.toLowerCase().trim() === "live" && (hour < 5 || hour > 16)) return false;
                        if (shiftFilter.toLowerCase().trim() === "offline" && (hour >= 5 && hour <= 16)) return false;
                    }
                }
            }
        }

        // Location filter
        if (locationFilter && locationFilter !== "All") {
            const reviewer = String(f.reviewer_name || f.assigned_to || "").toLowerCase().trim();
            const matchedUser = usersList.find(u => String(u.name).toLowerCase().trim() === reviewer);
            const userLoc = matchedUser && matchedUser.location ? String(matchedUser.location).toLowerCase().trim() : "";
            if (userLoc !== locationFilter.toLowerCase().trim()) return false;
        }

        // Department filter (All / QC / Revised)
        if (deptFilter && deptFilter !== "All") {
            const rawDept = String(f.department || f.activeDept || "QC").toLowerCase().trim();
            const isRevised = rawDept.includes("revised") || rawDept.includes("report");
            if (deptFilter === "Revised" && !isRevised) return false;
            if (deptFilter === "QC" && isRevised) return false;
        }
        return true;
    };

    // SLA Targets for different clients (hours)
    const SLA_TARGETS = {
        "A1 AMC": 2, "AAG": 2, "AAMS": 4, "All Pro AMC": 2,
        "AMCSSC": 2, "Appraisal Partner": 4, "Appraisers Review": 2,
        "Ascribe VL": 4, "Core Valuation": 2, "EA - Appraisals": 2, "Eppraisal": 4,
        "Exactus": 1, "Financial Dimensions": 4, "Inspectify - Hybrid": 1,
        "LRES - Appraisals": 2, "LVS": 4, "NAN": 2, "Property Valuation Network": 4,
        "Signature": 2, "Team Matthew Appraisals": 1,
        "Top Notch Appraisers": 1, "Valora AMC": 2,
        "Valutrust Solutions": 2, "Zap Appraisal": 2
    };

    // English month name helper
    const getMonthName = (dateStr) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        const d = new Date(year, month - 1, day);
        return d.toLocaleString("default", { month: "long" });
    };

    // Format date as DD-MM-YYYY helper
    const getFormattedDate = (dateStr) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${day}-${month}-${year}`;
    };

    // 1. Compute SLA Compliance Report (Shift date for Inflow, calendar date for Outflow/SLA)
    const slaComplianceData = useMemo(() => {
        let rawClients = Object.keys(SLA_TARGETS);
        // Case-insensitive deduplication so duplicate variations like "Zap Appraisal" and "Zap appraisal" don't create two rows
        const uniqueClientMap = new Map();
        rawClients.forEach(c => {
            const key = c.toLowerCase().trim();
            if (!uniqueClientMap.has(key)) {
                uniqueClientMap.set(key, c);
            }
        });
        let clients = Array.from(uniqueClientMap.values());

        if (selectedClient && selectedClient !== "All" && selectedClient !== "") {
            let selectedName = selectedClient;
            if (clientsList && clientsList.length > 0) {
                const found = clientsList.find(c => String(c.id) === String(selectedClient) || c.name.toLowerCase().trim() === String(selectedClient).toLowerCase().trim());
                if (found) selectedName = found.name;
            }
            const normSel = String(selectedName).toLowerCase().trim();
            clients = clients.filter(c => c.toLowerCase().trim() === normSel);
            if (clients.length === 0) {
                clients = [selectedName];
            }
        }
        const sourceFiles = (allFiles && allFiles.length > 0) ? allFiles : dashboardFiles;

        const report = clients.map(clientName => {
            const clientFiles = sourceFiles.filter(f => {
                const nameMatch = f.client_name && f.client_name.toLowerCase().trim() === clientName.toLowerCase().trim();
                const cleanMatch = f.client_clean && f.client_clean.toLowerCase().trim() === clientName.toLowerCase().trim();
                return nameMatch || cleanMatch;
            });

            // Inflow: count received files using shifted in_time (5 PM rollover on in_time: 17:00+ rolls over to next day)
            const inflowFiles = clientFiles.filter(f => {
                const inflowShiftDate = getShiftDate(f.in_time);
                const isDateMatch = inflowShiftDate >= selectedStartDateStr && inflowShiftDate <= selectedEndDateStr;
                return isDateMatch && matchesFilters(f, "in_time");
            });
            const inflow = inflowFiles.length;

            // Outflow: count completed files strictly using calendar date (date_column)
            const outflowFiles = clientFiles.filter(f => {
                const fDate = formatDate(f.date_column);
                const isDateMatch = fDate >= selectedStartDateStr && fDate <= selectedEndDateStr;
                const isCompleted = isClosedOrExceptionFile(f) || (f.out_time && String(f.out_time).trim() !== "" && String(f.out_time) !== "-");
                return isDateMatch && isCompleted && matchesFilters(f, "out_time");
            });
            const outflow = outflowFiles.length;

            // SLA targets evaluated on files matching calendar date range
            const slaBaseFiles = clientFiles.filter(f => {
                const fDate = formatDate(f.date_column);
                const isDateMatch = fDate >= selectedStartDateStr && fDate <= selectedEndDateStr;
                return isDateMatch && matchesFilters(f);
            });

            let withinSla = slaBaseFiles.filter(f => f.sla_status && f.sla_status.toLowerCase().includes("within")).length;
            let overSla = slaBaseFiles.filter(f => f.sla_status && f.sla_status.toLowerCase().includes("over")).length;

            const totalEvaluated = withinSla + overSla;
            const slaPercent = totalEvaluated > 0 ? Math.round((withinSla / totalEvaluated) * 100) : null;
            
            let status = "-";
            if (slaPercent !== null) {
                if (slaPercent >= 95) status = "On Track";
                else if (slaPercent >= 85) status = "At Risk";
                else status = "Breached";
            }

            const targetSla = SLA_TARGETS[clientName] || 
                Object.entries(SLA_TARGETS).find(([k]) => k.toLowerCase().trim() === clientName.toLowerCase().trim())?.[1] || 
                2;

            return {
                clientName,
                targetSla,
                inflow,
                outflow,
                withinSla,
                overSla,
                slaPercent,
                status
            };
        });

        // Totals Row
        const totalInflowSum = report.reduce((acc, curr) => acc + curr.inflow, 0);
        const totalOutflowSum = report.reduce((acc, curr) => acc + curr.outflow, 0);
        const totalWithinSlaSum = report.reduce((acc, curr) => acc + curr.withinSla, 0);
        const totalOverSlaSum = report.reduce((acc, curr) => acc + curr.overSla, 0);
        
        const totalEvaluatedSum = totalWithinSlaSum + totalOverSlaSum;
        const averageSla = totalEvaluatedSum > 0 ? Math.round((totalWithinSlaSum / totalEvaluatedSum) * 100) : 0;

        return { 
            list: report.filter(r => r.inflow > 0 || r.outflow > 0),
            totals: { totalInflowSum, totalOutflowSum, totalWithinSlaSum, totalOverSlaSum, averageSla } 
        };
    }, [dashboardFiles, selectedStartDateStr, selectedEndDateStr, shiftFilter, locationFilter, usersList, selectedClient, clientsList, deptFilter]);

    // Compute number of days selected in date range
    const numDays = useMemo(() => {
        if (!selectedStartDateStr || !selectedEndDateStr) return 1;
        const d1 = new Date(selectedStartDateStr);
        const d2 = new Date(selectedEndDateStr);
        if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 1;
        const diffDays = Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        return diffDays > 0 ? diffDays : 1;
    }, [selectedStartDateStr, selectedEndDateStr]);

    // 2. Compute Reviewer Productivity (using calendar date)
    const reviewerProductivityData = useMemo(() => {
        const activeUsers = usersList.length > 0 ? usersList : [
            ...new Set(dashboardFiles.map(f => f.reviewer_name).filter(Boolean))
        ].map(name => ({ name }));

        const filteredUsers = locationFilter && locationFilter !== "All"
            ? activeUsers.filter(u => u.location && String(u.location).toLowerCase().trim() === locationFilter.toLowerCase().trim())
            : activeUsers;

        const list = filteredUsers.map(user => {
            const name = user.name;
            const completedFiles = dashboardFiles.filter(f => {
                const reviewerMatch = f.reviewer_name && f.reviewer_name.toLowerCase().trim() === name.toLowerCase().trim();
                if (!reviewerMatch) return false;
                
                const fDate = formatDate(f.date_column);
                const isDateMatch = fDate >= selectedStartDateStr && fDate <= selectedEndDateStr;
                const isCompleted = f.out_time && String(f.out_time).trim() !== "" && String(f.out_time) !== "-";
                return isDateMatch && isCompleted && matchesFilters(f);
            });
            const volume = completedFiles.length;

            const isPresent = volume > 0 ? "Y" : "N";
            const hoursWorked = isPresent === "Y" ? 8 : 0;

            let shift = "Night";
            if (completedFiles.length > 0) {
                const avgHour = completedFiles.reduce((acc, f) => {
                    const d = new Date(f.out_time);
                    return acc + (isNaN(d.getTime()) ? 12 : d.getHours());
                }, 0) / completedFiles.length;
                if (avgHour >= 9 && avgHour < 18) shift = "Day";
                else if (avgHour >= 18 || avgHour < 3) shift = "Mid";
            }

            const matchedUser = usersList.find(u => String(u.name || "").toLowerCase().trim() === name.toLowerCase().trim());
            const customTarget = matchedUser && matchedUser.daily_target && matchedUser.daily_target > 0 ? parseFloat(matchedUser.daily_target) : null;
            const dailyTarget = customTarget !== null ? customTarget : 12;
            
            const workedDaysCount = new Set(completedFiles.map(f => formatDate(f.date_column || f.out_time)).filter(Boolean)).size;
            const activeDays = workedDaysCount > 0 ? workedDaysCount : 1;
            const target = dailyTarget * activeDays;

            const qcCountVal = completedFiles.filter(f => {
                const dept = String(f.department || f.activeDept || "").toLowerCase().trim();
                return dept.includes("qc") || (!dept.includes("revised") && !dept.includes("report"));
            }).length;

            const revisedCountVal = completedFiles.filter(f => {
                const dept = String(f.department || f.activeDept || "").toLowerCase().trim();
                return dept.includes("revised") || dept.includes("report");
            }).length;

            const weightedVolume = qcCountVal + (revisedCountVal / 4);

            // Quality calculation
            const qualityBreaches = completedFiles.filter(f => f.feedback || f.auditor_findings).length;
            const extQuality = volume > 0 ? Math.round(((volume - qualityBreaches) / volume) * 100) : 100;

            const unit = volume > 0 ? parseFloat((weightedVolume * (extQuality / 100)).toFixed(2)) : 0;
            const productivity = target > 0 ? Math.round((unit / target) * 100) : 0;

            return {
                name,
                shift,
                dailyTarget,
                target,
                unit,
                volume,
                productivity,
                extQuality,
                isPresent,
                hoursWorked
            };
        });

        // Filter out employees that didn't complete files and sort by volume descending
        const sortedList = list.filter(r => r.volume > 0).sort((a, b) => b.volume - a.volume);

        return { list: sortedList };
    }, [dashboardFiles, selectedStartDateStr, selectedEndDateStr, shiftFilter, locationFilter, usersList, numDays, deptFilter]);

    // 3. Compute Individual Employee Detailed Performance & Client Breakdown
    const availableEmployees = useMemo(() => {
        const setOfNames = new Set();
        if (reviewerProductivityData && reviewerProductivityData.list) {
            reviewerProductivityData.list.forEach(r => setOfNames.add(r.name));
        }
        if (usersList && usersList.length > 0) {
            usersList.forEach(u => u.name && setOfNames.add(u.name));
        }
        dashboardFiles.forEach(f => {
            const name = f.reviewer_name || f.assigned_to;
            if (name) setOfNames.add(name);
        });
        return Array.from(setOfNames).sort((a, b) => a.localeCompare(b));
    }, [reviewerProductivityData, usersList, dashboardFiles]);

    const employeeDetailedData = useMemo(() => {
        const getEmpStats = (empName) => {
            const completedFiles = dashboardFiles.filter(f => {
                const reviewer = (f.reviewer_name || f.assigned_to || "").toLowerCase().trim();
                if (reviewer !== empName.toLowerCase().trim()) return false;

                const fDate = formatDate(f.date_column);
                const isDateMatch = fDate >= selectedStartDateStr && fDate <= selectedEndDateStr;
                const isCompleted = f.out_time && String(f.out_time).trim() !== "" && String(f.out_time) !== "-";
                return isDateMatch && isCompleted && matchesFilters(f);
            });

            const totalCompleted = completedFiles.length;

            const qcFiles = completedFiles.filter(f => {
                const dept = String(f.department || f.activeDept || "").toLowerCase().trim();
                return dept.includes("qc") || (!dept.includes("revised") && !dept.includes("report"));
            });
            const revisedFiles = completedFiles.filter(f => {
                const dept = String(f.department || f.activeDept || "").toLowerCase().trim();
                return dept.includes("revised") || dept.includes("report");
            });

            const qcCount = qcFiles.length;
            const revisedCount = revisedFiles.length;

            const withinSlaCount = completedFiles.filter(f => f.sla_status && String(f.sla_status).toLowerCase().includes("within")).length;
            const overSlaCount = completedFiles.filter(f => f.sla_status && String(f.sla_status).toLowerCase().includes("over")).length;

            const totalEvaluatedSla = withinSlaCount + overSlaCount;
            const slaPercent = totalEvaluatedSla > 0 ? Math.round((withinSlaCount / totalEvaluatedSla) * 100) : null;

            // Utilization / Productivity calculation
            let shift = "Night";
            if (completedFiles.length > 0) {
                const avgHour = completedFiles.reduce((acc, f) => {
                    const d = new Date(f.out_time);
                    return acc + (isNaN(d.getTime()) ? 12 : d.getHours());
                }, 0) / completedFiles.length;
                if (avgHour >= 9 && avgHour < 18) shift = "Day";
                else if (avgHour >= 18 || avgHour < 3) shift = "Mid";
            }
            const matchedUser = usersList.find(u => String(u.name || "").toLowerCase().trim() === empName.toLowerCase().trim());
            const customTarget = matchedUser && matchedUser.daily_target && matchedUser.daily_target > 0 ? parseFloat(matchedUser.daily_target) : null;
            const dailyTarget = customTarget !== null ? customTarget : 12;

            const workedDaysCount = new Set(completedFiles.map(f => formatDate(f.date_column || f.out_time)).filter(Boolean)).size;
            const activeDays = workedDaysCount > 0 ? workedDaysCount : 1;
            const target = dailyTarget * activeDays;
            const weightedUnits = qcCount + (revisedCount / 4);
            const qualityBreaches = completedFiles.filter(f => f.feedback || f.auditor_findings).length;
            const extQuality = totalCompleted > 0 ? Math.round(((totalCompleted - qualityBreaches) / totalCompleted) * 100) : 100;
            const unit = totalCompleted > 0 ? parseFloat((weightedUnits * (extQuality / 100)).toFixed(2)) : 0;
            const utilization = target > 0 ? Math.round((unit / target) * 100) : 0;

            // Per-client breakdown for this employee
            const clientsMap = {};
            completedFiles.forEach(f => {
                const clientName = f.client_name || f.activeClient || "Unknown Client";
                if (!clientsMap[clientName]) {
                    clientsMap[clientName] = {
                        clientName,
                        qcCount: 0,
                        revisedCount: 0,
                        totalCount: 0,
                        withinSla: 0,
                        overSla: 0
                    };
                }
                const dept = String(f.department || f.activeDept || "").toLowerCase().trim();
                if (dept.includes("revised") || dept.includes("report")) {
                    clientsMap[clientName].revisedCount += 1;
                } else {
                    clientsMap[clientName].qcCount += 1;
                }
                clientsMap[clientName].totalCount += 1;

                if (f.sla_status && String(f.sla_status).toLowerCase().includes("within")) {
                    clientsMap[clientName].withinSla += 1;
                } else if (f.sla_status && String(f.sla_status).toLowerCase().includes("over")) {
                    clientsMap[clientName].overSla += 1;
                }
            });

            const clientBreakdown = Object.values(clientsMap).map(c => {
                const evaluated = c.withinSla + c.overSla;
                const slaPct = evaluated > 0 ? Math.round((c.withinSla / evaluated) * 100) : null;
                return { ...c, slaPct };
            }).sort((a, b) => b.totalCount - a.totalCount);

            return {
                empName,
                totalCompleted,
                qcCount,
                revisedCount,
                withinSlaCount,
                overSlaCount,
                slaPercent,
                shift,
                dailyTarget,
                target,
                utilization,
                clientBreakdown
            };
        };

        if (selectedEmployeeFilter && selectedEmployeeFilter !== "All") {
            return [getEmpStats(selectedEmployeeFilter)];
        }

        // If "All" is selected, return stats for all employees who completed files
        const employeesWithFiles = availableEmployees.filter(emp => {
            const stats = getEmpStats(emp);
            return stats.totalCompleted > 0;
        });

        return employeesWithFiles.map(emp => getEmpStats(emp));
    }, [selectedEmployeeFilter, availableEmployees, dashboardFiles, selectedStartDateStr, selectedEndDateStr, shiftFilter, locationFilter, matchesFilters, numDays, deptFilter]);

    const handlePrint = () => {
        window.print();
    };

    const handleExportCSV = () => {
        const csvRows = [];

        const escapeCsv = (val) => {
            if (val === null || val === undefined) return '""';
            const str = String(val).replace(/"/g, '""');
            return `"${str}"`;
        };

        // Title & Range
        csvRows.push([escapeCsv(`DAILY PERFORMANCE REPORT - ${displayDateRangeStr}`)]);
        csvRows.push([escapeCsv("Generated On"), escapeCsv(new Date().toLocaleString())]);
        csvRows.push([]);

        // SECTION 1: CLIENT PERFORMANCE & SLA COMPLIANCE
        csvRows.push([escapeCsv("--- SECTION 1: CLIENT PERFORMANCE & SLA COMPLIANCE ---")]);
        csvRows.push([
            escapeCsv("Client Name"),
            escapeCsv("SLA Target (hrs)"),
            escapeCsv("Inflow (Received)"),
            escapeCsv("Outflow (Completed)"),
            escapeCsv("Within SLA"),
            escapeCsv("Over SLA"),
            escapeCsv("SLA %"),
            escapeCsv("Status")
        ]);
        
        slaComplianceData.list.forEach(row => {
            csvRows.push([
                escapeCsv(row.clientName),
                escapeCsv(`${row.targetSla}h`),
                escapeCsv(row.inflow),
                escapeCsv(row.outflow),
                escapeCsv(row.withinSla),
                escapeCsv(row.overSla),
                escapeCsv(row.slaPercent !== null ? `${row.slaPercent}%` : "-"),
                escapeCsv(row.status)
            ]);
        });
        // Totals row for Section 1
        csvRows.push([
            escapeCsv("Total / Summary"),
            escapeCsv("-"),
            escapeCsv(slaComplianceData.totals.totalInflowSum),
            escapeCsv(slaComplianceData.totals.totalOutflowSum),
            escapeCsv(slaComplianceData.totals.totalWithinSlaSum),
            escapeCsv(slaComplianceData.totals.totalOverSlaSum),
            escapeCsv(`${slaComplianceData.totals.averageSla}%`),
            escapeCsv("-")
        ]);
        csvRows.push([]);
        csvRows.push([]);

        // SECTION 2: EMPLOYEE PERFORMANCE & COMPLETED FILES COUNT
        csvRows.push([escapeCsv("--- SECTION 2: EMPLOYEE PERFORMANCE & COMPLETED FILES COUNT ---")]);
        csvRows.push([
            escapeCsv("Employee Name"),
            escapeCsv("Daily Target"),
            escapeCsv("MTD Target"),
            escapeCsv("Completed Files Count"),
            escapeCsv("Utilization %")
        ]);
        reviewerProductivityData.list.forEach(row => {
            csvRows.push([
                escapeCsv(row.name),
                escapeCsv(`${row.dailyTarget} files`),
                escapeCsv(`${row.target} files`),
                escapeCsv(`${row.volume} files`),
                escapeCsv(`${row.productivity}%`)
            ]);
        });
        csvRows.push([]);
        csvRows.push([]);

        // SECTION 3: EMPLOYEE DETAILED CLIENT & SLA PERFORMANCE
        csvRows.push([escapeCsv("--- SECTION 3: EMPLOYEE DETAILED CLIENT & SLA PERFORMANCE ---")]);
        csvRows.push([
            escapeCsv("Employee Name"),
            escapeCsv("Client Name"),
            escapeCsv("QC Files"),
            escapeCsv("Revised Files"),
            escapeCsv("Total Files"),
            escapeCsv("Within SLA"),
            escapeCsv("Over SLA"),
            escapeCsv("SLA %"),
            escapeCsv("Daily Target"),
            escapeCsv("MTD Target"),
            escapeCsv("Utilization %")
        ]);

        if (employeeDetailedData && employeeDetailedData.length > 0) {
            employeeDetailedData.forEach(emp => {
                if (emp.clientBreakdown && emp.clientBreakdown.length > 0) {
                    emp.clientBreakdown.forEach(c => {
                        csvRows.push([
                            escapeCsv(emp.empName),
                            escapeCsv(c.clientName),
                            escapeCsv(c.qcCount),
                            escapeCsv(c.revisedCount),
                            escapeCsv(c.totalCount),
                            escapeCsv(c.withinSla),
                            escapeCsv(c.overSla),
                            escapeCsv(c.slaPct !== null ? `${c.slaPct}%` : "-"),
                            escapeCsv(`${emp.dailyTarget} files`),
                            escapeCsv(`${emp.target} files`),
                            escapeCsv(`${emp.utilization}%`)
                        ]);
                    });
                } else {
                    csvRows.push([
                        escapeCsv(emp.empName),
                        escapeCsv("No client records"),
                        escapeCsv(0),
                        escapeCsv(0),
                        escapeCsv(0),
                        escapeCsv(0),
                        escapeCsv(0),
                        escapeCsv("-"),
                        escapeCsv(`${emp.dailyTarget} files`),
                        escapeCsv(`${emp.target} files`),
                        escapeCsv(`${emp.utilization}%`)
                    ]);
                }
            });
        }

        const csvString = "\uFEFF" + csvRows.map(row => row.join(",")).join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Daily_Performance_Report_${selectedStartDateStr}_to_${selectedEndDateStr}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const displayDateRangeStr = useMemo(() => {
        if (selectedStartDateStr === selectedEndDateStr) {
            return getFormattedDate(selectedStartDateStr);
        }
        return `${getFormattedDate(selectedStartDateStr)} to ${getFormattedDate(selectedEndDateStr)}`;
    }, [selectedStartDateStr, selectedEndDateStr]);

    return (
        <div style={{ padding: "24px", backgroundColor: "#f8fafc", minHeight: "100vh" }}>
            
            {/* Styles for print-friendly layouts */}
            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    /* Expand containers and disable layout constraints causing clipping/scrollbars */
                    html, body, .main-portal-container, main {
                        height: auto !important;
                        min-height: auto !important;
                        overflow: visible !important;
                        position: static !important;
                        display: block !important;
                        width: 100% !important;
                        box-sizing: border-box !important;
                    }
                    body {
                        background-color: white !important;
                        color: black !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .top-filter-bar-print-hide {
                        display: none !important;
                    }
                    .pdf-card {
                        box-shadow: none !important;
                        border: 1px solid #cbd5e1 !important;
                        margin-bottom: 20px !important;
                        page-break-inside: avoid !important;
                    }
                    .pdf-grid-row {
                        display: flex !important;
                        gap: 10px !important;
                        grid-template-columns: none !important;
                    }
                    .pdf-grid-card {
                        flex: 1 !important;
                        border: 1px solid #cbd5e1 !important;
                        padding: 10px !important;
                    }
                    .print-only-block {
                        display: block !important;
                    }
                    input::placeholder {
                        color: transparent !important;
                    }
                }
                .print-only-block {
                    display: none;
                }
                /* Explicitly enable mouse text selection inside tables for copy-pasting */
                table, th, td {
                    user-select: text !important;
                    -webkit-user-select: text !important;
                    -moz-user-select: text !important;
                    -ms-user-select: text !important;
                }
            `}} />

            {/* Header Toolbar */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }} className="top-filter-bar-print-hide">
                <div>
                    <h2 style={{ fontSize: "22px", fontWeight: "800", color: "#0f172a", margin: 0 }}>Daily Performance Report</h2>
                    <p style={{ fontSize: "12px", color: "#64748b", margin: "4px 0 0 0" }}>
                        Overview of operations for: <b>{displayDateRangeStr}</b>
                    </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "700", color: "#64748b" }}>Department:</label>
                        <div style={{ display: "flex", gap: "4px", backgroundColor: "#e2e8f0", padding: "4px", borderRadius: "8px" }}>
                            {[
                                { value: "All", label: "All" },
                                { value: "QC", label: "QC" },
                                { value: "Revised", label: "Revised" }
                            ].map(btn => (
                                <button
                                    key={btn.value}
                                    type="button"
                                    onClick={() => setDeptFilter(btn.value)}
                                    style={{
                                        padding: "6px 14px",
                                        fontSize: "12px",
                                        fontWeight: "800",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        backgroundColor: deptFilter === btn.value ? "#00a3e0" : "white",
                                        color: deptFilter === btn.value ? "white" : "#475569",
                                        boxShadow: deptFilter === btn.value ? "0 1px 2px rgba(0,0,0,0.1)" : "none"
                                    }}
                                >
                                    {btn.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button
                        onClick={handleExportCSV}
                        style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px", backgroundColor: "#10b981", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer", transition: "background 0.2s" }}
                    >
                        <Download size={16} /> Export to CSV
                    </button>
                </div>
            </div>

            {/* Print Header Block */}
            <div className="print-only-block">
                <div style={{ textAlign: "center", paddingBottom: "15px", borderBottom: "2px solid #0f172a", marginBottom: "20px" }}>
                    <h1 style={{ fontSize: "24px", fontWeight: "800", margin: "0 0 5px 0" }}>GO-SOURCE OPERATIONS PORTAL</h1>
                    <h2 style={{ fontSize: "16px", fontWeight: "600", margin: 0, color: "#475569" }}>Daily Executive Performance Report</h2>
                    <p style={{ fontSize: "12px", margin: "5px 0 0 0", fontWeight: "bold" }}>Date Range: {displayDateRangeStr} ({getMonthName(selectedStartDateStr)})</p>
                </div>
            </div>

            {/* Consolidated KPI Dashboard Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "12px", marginBottom: "24px" }} className="pdf-grid-row">
                
                {/* Month Card */}
                <div style={{ backgroundColor: "#fdf2f8", border: "1px solid #fbcfe8", borderRadius: "10px", padding: "12px", textAlign: "center" }} className="pdf-grid-card">
                    <div style={{ fontSize: "10px", fontWeight: "700", color: "#9d174d", textTransform: "uppercase", letterSpacing: "0.5px" }}>Month</div>
                    <div style={{ fontSize: "18px", fontWeight: "800", color: "#831843", marginTop: "4px" }}>{getMonthName(selectedStartDateStr)}</div>
                </div>

                {/* Date Card */}
                <div style={{ backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "12px", textAlign: "center" }} className="pdf-grid-card">
                    <div style={{ fontSize: "10px", fontWeight: "700", color: "#1e40af", textTransform: "uppercase", letterSpacing: "0.5px" }}>Date Range</div>
                    <div style={{ fontSize: "12px", fontWeight: "800", color: "#1e3a8a", marginTop: "8px", wordBreak: "break-all" }}>{displayDateRangeStr}</div>
                </div>

                {/* Inflow Card */}
                <div style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "12px", textAlign: "center" }} className="pdf-grid-card">
                    <div style={{ fontSize: "10px", fontWeight: "700", color: "#166534", textTransform: "uppercase", letterSpacing: "0.5px", display: "flex", alignItems: "center", justifyContent: "center", gap: "2px" }}>
                        Total Inflow
                    </div>
                    <div style={{ fontSize: "20px", fontWeight: "900", color: "#14532d", marginTop: "4px" }}>{slaComplianceData.totals.totalInflowSum}</div>
                </div>

                {/* Outflow Card */}
                <div style={{ backgroundColor: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "10px", padding: "12px", textAlign: "center" }} className="pdf-grid-card">
                    <div style={{ fontSize: "10px", fontWeight: "700", color: "#5b21b6", textTransform: "uppercase", letterSpacing: "0.5px", display: "flex", alignItems: "center", justifyContent: "center", gap: "2px" }}>
                        Total Outflow
                    </div>
                    <div style={{ fontSize: "20px", fontWeight: "900", color: "#4c1d95", marginTop: "4px" }}>{slaComplianceData.totals.totalOutflowSum}</div>
                </div>

                {/* Within SLA Card */}
                <div style={{ backgroundColor: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: "10px", padding: "12px", textAlign: "center" }} className="pdf-grid-card">
                    <div style={{ fontSize: "10px", fontWeight: "700", color: "#065f46", textTransform: "uppercase", letterSpacing: "0.5px" }}>Within SLA</div>
                    <div style={{ fontSize: "20px", fontWeight: "900", color: "#064e3b", marginTop: "4px" }}>{slaComplianceData.totals.totalWithinSlaSum}</div>
                </div>

                {/* Over SLA Card */}
                <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "12px", textAlign: "center" }} className="pdf-grid-card">
                    <div style={{ fontSize: "10px", fontWeight: "700", color: "#991b1b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Over SLA</div>
                    <div style={{ fontSize: "20px", fontWeight: "900", color: "#7f1d1d", marginTop: "4px" }}>{slaComplianceData.totals.totalOverSlaSum}</div>
                </div>

                {/* SLA % Card */}
                <div style={{ backgroundColor: "#fffbeb", border: "1px solid #fef3c7", borderRadius: "10px", padding: "12px", textAlign: "center" }} className="pdf-grid-card">
                    <div style={{ fontSize: "10px", fontWeight: "700", color: "#92400e", textTransform: "uppercase", letterSpacing: "0.5px", display: "flex", alignItems: "center", justifyContent: "center", gap: "2px" }}>
                        Combined SLA %
                    </div>
                    <div style={{ fontSize: "20px", fontWeight: "900", color: "#78350f", marginTop: "4px" }}>{slaComplianceData.totals.averageSla}%</div>
                </div>

            </div>

            {/* Layout Tables Grid */}
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                
                {/* 1. Client SLA Compliance Table */}
                <div style={{ backgroundColor: "white", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }} className="pdf-card">
                    <h3 style={{ fontSize: "14px", fontWeight: "800", color: "#1e293b", marginTop: 0, marginBottom: "16px", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px" }}>
                        CLIENT PERFORMANCE & SLA COMPLIANCE
                    </h3>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                            <thead>
                                <tr style={{ backgroundColor: "#0f172a", color: "white", textAlign: "left" }}>
                                    <th style={{ padding: "10px", border: "1px solid #334155" }}>Client Name</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>SLA Target (hrs)</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>Inflow (Received)</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>Outflow (Completed)</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>Within SLA</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>Over SLA</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>SLA %</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {slaComplianceData.list.length > 0 ? (
                                    slaComplianceData.list.map((row) => {
                                        let statusColor = "#64748b";
                                        let StatusIcon = null;

                                        if (row.status === "On Track") {
                                            statusColor = "#16a34a";
                                            StatusIcon = <CheckCircle2 size={13} style={{ color: "#16a34a" }} />;
                                        } else if (row.status === "At Risk") {
                                            statusColor = "#d97706";
                                            StatusIcon = <AlertTriangle size={13} style={{ color: "#d97706" }} />;
                                        } else if (row.status === "Breached") {
                                            statusColor = "#dc2626";
                                            StatusIcon = <XCircle size={13} style={{ color: "#dc2626" }} />;
                                        }

                                        return (
                                            <tr key={row.clientName} style={{ borderBottom: "1px solid #cbd5e1" }}>
                                                <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", fontWeight: "600" }}>{row.clientName}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", textAlign: "center" }}>{row.targetSla}h</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", textAlign: "center", fontWeight: "600" }}>{row.inflow}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", textAlign: "center", fontWeight: "600" }}>{row.outflow}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", textAlign: "center", color: row.withinSla > 0 ? "#16a34a" : "#64748b" }}>{row.inflow > 0 || row.outflow > 0 ? row.withinSla : "-"}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", textAlign: "center", color: row.overSla > 0 ? "#dc2626" : "#64748b" }}>{row.inflow > 0 || row.outflow > 0 ? row.overSla : "-"}</td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", textAlign: "center", fontWeight: "700" }}>
                                                    {row.slaPercent !== null ? `${row.slaPercent}%` : "-"}
                                                </td>
                                                <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1" }}>
                                                    {row.status !== "-" ? (
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: statusColor, fontWeight: "700", fontSize: "11px" }}>
                                                            {StatusIcon} {row.status}
                                                        </span>
                                                    ) : "-"}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="8" style={{ padding: "16px", textAlign: "center", color: "#64748b" }}>No active records found for the selected date.</td>
                                    </tr>
                                )}
                                <tr style={{ fontWeight: "800", backgroundColor: "#e2e8f0" }}>
                                    <td style={{ padding: "10px", border: "1px solid #cbd5e1" }}>Total / Summary</td>
                                    <td style={{ padding: "10px", border: "1px solid #cbd5e1", textAlign: "center" }}>-</td>
                                    <td style={{ padding: "10px", border: "1px solid #cbd5e1", textAlign: "center" }}>{slaComplianceData.totals.totalInflowSum}</td>
                                    <td style={{ padding: "10px", border: "1px solid #cbd5e1", textAlign: "center" }}>{slaComplianceData.totals.totalOutflowSum}</td>
                                    <td style={{ padding: "10px", border: "1px solid #cbd5e1", textAlign: "center" }}>{slaComplianceData.totals.totalWithinSlaSum}</td>
                                    <td style={{ padding: "10px", border: "1px solid #cbd5e1", textAlign: "center" }}>{slaComplianceData.totals.totalOverSlaSum}</td>
                                    <td style={{ padding: "10px", border: "1px solid #cbd5e1", textAlign: "center" }}>{slaComplianceData.totals.averageSla}%</td>
                                    <td style={{ padding: "10px", border: "1px solid #cbd5e1", textAlign: "center" }}>-</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 2. Employee Completed Files Count Table */}
                <div style={{ backgroundColor: "white", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }} className="pdf-card">
                    <h3 style={{ fontSize: "14px", fontWeight: "800", color: "#1e293b", marginTop: 0, marginBottom: "16px", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px" }}>
                        EMPLOYEE PERFORMANCE & COMPLETED FILES COUNT
                    </h3>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                            <thead>
                                <tr style={{ backgroundColor: "#0f172a", color: "white", textAlign: "left" }}>
                                    <th style={{ padding: "10px", border: "1px solid #334155" }}>Employee Name</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>Daily Target</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>MTD Target</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>Completed Files Count</th>
                                    <th style={{ padding: "10px", border: "1px solid #334155", textAlign: "center" }}>Utilization %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviewerProductivityData.list.length > 0 ? (
                                    reviewerProductivityData.list.map((row) => (
                                        <tr key={row.name} style={{ borderBottom: "1px solid #cbd5e1" }}>
                                            <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", fontWeight: "600" }}>{row.name}</td>
                                            <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", textAlign: "center", fontWeight: "600", color: "#475569" }}>{row.dailyTarget} files</td>
                                            <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", textAlign: "center", fontWeight: "600", color: "#475569" }}>{row.target} files</td>
                                            <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", textAlign: "center", fontWeight: "700", color: "#3b82f6" }}>{row.volume} files</td>
                                            <td style={{ padding: "8px 10px", border: "1px solid #cbd5e1", textAlign: "center", fontWeight: "800", color: row.productivity >= 100 ? "#16a34a" : "#d97706" }}>{row.productivity}%</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ padding: "16px", textAlign: "center", color: "#64748b" }}>No employees with completed files found on this date.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 3. Individual Employee Detailed Performance & Client Breakdown Table */}
                <div style={{ backgroundColor: "white", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }} className="pdf-card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", borderBottom: "2px solid #e2e8f0", paddingBottom: "10px", flexWrap: "wrap", gap: "10px" }}>
                        <h3 style={{ fontSize: "14px", fontWeight: "800", color: "#1e293b", margin: 0 }}>
                            EMPLOYEE DETAILED CLIENT & SLA PERFORMANCE
                        </h3>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }} className="top-filter-bar-print-hide">
                            <label style={{ fontSize: "12px", fontWeight: "700", color: "#64748b" }}>Filter Employee:</label>
                            <select
                                value={selectedEmployeeFilter}
                                onChange={(e) => setSelectedEmployeeFilter(e.target.value)}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    color: "#0f172a",
                                    backgroundColor: "#f8fafc",
                                    outline: "none",
                                    cursor: "pointer"
                                }}
                            >
                                <option value="All">All Employees</option>
                                {availableEmployees.map(emp => (
                                    <option key={emp} value={emp}>{emp}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {employeeDetailedData && employeeDetailedData.length > 0 ? (
                        employeeDetailedData.map(emp => (
                            <div key={emp.empName} style={{ marginBottom: "20px", padding: "16px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "10px" }}>
                                    <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "800", color: "#0f172a" }}>
                                        👤 Employee: <span style={{ color: "#2563eb" }}>{emp.empName}</span>
                                    </h4>
                                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                        <span style={{ padding: "4px 10px", backgroundColor: "#f1f5f9", color: "#334155", borderRadius: "6px", fontSize: "11px", fontWeight: "700" }}>
                                            Daily Target: {emp.dailyTarget} files
                                        </span>
                                        <span style={{ padding: "4px 10px", backgroundColor: "#e0f2fe", color: "#0369a1", borderRadius: "6px", fontSize: "11px", fontWeight: "700" }}>
                                            MTD Target: {emp.target} files
                                        </span>
                                        <span style={{ padding: "4px 10px", backgroundColor: "#f3e8ff", color: "#6b21a8", borderRadius: "6px", fontSize: "11px", fontWeight: "800" }}>
                                            Utilization: {emp.utilization}%
                                        </span>
                                    </div>
                                </div>

                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", backgroundColor: "white" }}>
                                        <thead>
                                            <tr style={{ backgroundColor: "#1e293b", color: "white", textAlign: "left" }}>
                                                <th style={{ padding: "8px 10px", border: "1px solid #334155" }}>Client Name</th>
                                                <th style={{ padding: "8px 10px", border: "1px solid #334155", textAlign: "center" }}>QC Files Completed</th>
                                                <th style={{ padding: "8px 10px", border: "1px solid #334155", textAlign: "center" }}>Revised Files Completed</th>
                                                <th style={{ padding: "8px 10px", border: "1px solid #334155", textAlign: "center" }}>Total Completed</th>
                                                <th style={{ padding: "8px 10px", border: "1px solid #334155", textAlign: "center" }}>Within SLA</th>
                                                <th style={{ padding: "8px 10px", border: "1px solid #334155", textAlign: "center" }}>Over SLA</th>
                                                <th style={{ padding: "8px 10px", border: "1px solid #334155", textAlign: "center" }}>SLA %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {emp.clientBreakdown.length > 0 ? (
                                                emp.clientBreakdown.map(c => (
                                                    <tr key={c.clientName} style={{ borderBottom: "1px solid #cbd5e1" }}>
                                                        <td style={{ padding: "6px 10px", border: "1px solid #cbd5e1", fontWeight: "600" }}>{c.clientName}</td>
                                                        <td style={{ padding: "6px 10px", border: "1px solid #cbd5e1", textAlign: "center", color: "#2563eb", fontWeight: "600" }}>{c.qcCount}</td>
                                                        <td style={{ padding: "6px 10px", border: "1px solid #cbd5e1", textAlign: "center", color: "#d97706", fontWeight: "600" }}>{c.revisedCount}</td>
                                                        <td style={{ padding: "6px 10px", border: "1px solid #cbd5e1", textAlign: "center", fontWeight: "700" }}>{c.totalCount}</td>
                                                        <td style={{ padding: "6px 10px", border: "1px solid #cbd5e1", textAlign: "center", color: c.withinSla > 0 ? "#16a34a" : "#64748b" }}>{c.withinSla}</td>
                                                        <td style={{ padding: "6px 10px", border: "1px solid #cbd5e1", textAlign: "center", color: c.overSla > 0 ? "#dc2626" : "#64748b" }}>{c.overSla}</td>
                                                        <td style={{ padding: "6px 10px", border: "1px solid #cbd5e1", textAlign: "center", fontWeight: "700" }}>
                                                            {c.slaPct !== null ? `${c.slaPct}%` : "-"}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" style={{ padding: "12px", textAlign: "center", color: "#64748b" }}>No client records found for this employee.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: "20px", textAlign: "center", color: "#64748b" }}>
                            No employee records found for the selected filter and date range.
                        </div>
                    )}
                </div>

            </div>

        </div>
    );
};

export default DailyReports;
